import { useEffect, useState } from 'react';
import { Plus, TriangleAlert, X } from 'lucide-react';
import {
  getApiError,
  useCreateFeeStructureMutation,
  useUpdateFeeStructureMutation,
  type FeeStructure,
  type Group,
  type Term,
} from '@/store/api';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { formatMinor, parseMajorToMinor } from '@/lib/money';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  The fee structure editor.

  ── Why the amounts are held as strings while you type ─────────────────────

  A controlled field storing a parsed number cannot represent "250." — the
  moment the decimal point is typed it parses to 250 and the point is erased
  from under the cursor. So each line keeps the raw text and is converted on
  submit, which is also where the one error message about a bad amount belongs.

  ── Why the total is shown and not entered ─────────────────────────────────

  The API sums the lines itself and ignores any total a client sends. Showing
  the same sum live is how the person entering it sees that the two agree before
  a thousand invoices are raised against it.
*/

type DraftItem = { label: string; amount: string };

export interface StructureDraft {
  _id?: string;
  name: string;
  academicYear: string;
  term: Term;
  groupId: string | null;
  dueDate: string;
  items: DraftItem[];
}

export function emptyStructureDraft(): StructureDraft {
  const now = new Date();
  const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    name: '',
    academicYear: `${year}/${year + 1}`,
    term: 'term_1',
    groupId: null,
    dueDate: '',
    items: [{ label: '', amount: '' }],
  };
}

export function toDraft(structure: FeeStructure): StructureDraft {
  return {
    _id: structure._id,
    name: structure.name,
    academicYear: structure.academicYear,
    term: structure.term,
    groupId: structure.groupId,
    dueDate: structure.dueDate,
    items: structure.items.map((i) => ({ label: i.label, amount: formatMinor(i.amountMinor) })),
  };
}

export function StructureDialog({
  draft,
  groups,
  institutionId,
  onClose,
}: {
  draft: StructureDraft | null;
  groups: Group[];
  institutionId: string;
  onClose: () => void;
}) {
  const [form, setForm] = useState<StructureDraft>(draft ?? emptyStructureDraft());
  const [error, setError] = useState('');

  const [createStructure, createState] = useCreateFeeStructureMutation();
  const [updateStructure, updateState] = useUpdateFeeStructureMutation();
  const saving = createState.isLoading || updateState.isLoading;
  const isEditing = Boolean(form._id);

  useEffect(() => {
    if (draft) setForm(draft);
    setError('');
  }, [draft]);

  const setItem = (index: number, patch: Partial<DraftItem>) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { label: '', amount: '' }] }));

  const removeItem = (index: number) =>
    setForm((f) => ({
      ...f,
      items: f.items.length === 1 ? f.items : f.items.filter((_, i) => i !== index),
    }));

  /* Lines that are still blank are ignored in the running total rather than
     counted as zero, so an empty row you have not filled in yet does not read
     as a line worth nothing. */
  const parsed = form.items.map((i) => ({ label: i.label.trim(), minor: parseMajorToMinor(i.amount) }));
  const totalMinor = parsed.reduce((sum, i) => sum + (i.minor ?? 0), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const usable = parsed.filter((i) => i.label !== '' || i.minor !== null);
    if (usable.length === 0) return setError('Add at least one fee line.');

    const unnamed = usable.find((i) => i.label === '');
    if (unnamed) return setError('Every fee line needs a name.');

    const badAmount = usable.find((i) => i.minor === null);
    if (badAmount) return setError(`"${badAmount.label}" needs an amount, like 250 or 250.00.`);

    if (!form.name.trim()) return setError('Give the structure a name.');
    if (!form.dueDate) return setError('Set a due date — it is what the arrears list is measured against.');

    const data = {
      name: form.name.trim(),
      academicYear: form.academicYear.trim(),
      term: form.term,
      groupId: form.groupId,
      dueDate: form.dueDate,
      items: usable.map((i) => ({ label: i.label, amountMinor: i.minor as number })),
    };

    try {
      if (form._id) {
        await updateStructure({ institutionId, structureId: form._id, data }).unwrap();
      } else {
        await createStructure({ institutionId, data }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <Modal
      open={draft !== null}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Edit fee structure' : 'New fee structure'}
      description="What a class owes for one term. Invoices copy these lines when they are raised, so editing this later does not change bills already issued."
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-muted-foreground">
            Total <span className={cn(styles.figure, 'text-[12.5px] text-foreground')}>GHS {formatMinor(totalMinor)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button form="structure-form" type="submit" loading={saving}>
              {isEditing ? 'Save changes' : 'Create structure'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="structure-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <div className={cn(styles.markDanger, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
            <TriangleAlert size={12} className="flex-shrink-0" />
            {error}
          </div>
        )}

        <Field label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Term 1 fees — Primary"
            autoFocus
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Academic year" required>
            <Input
              value={form.academicYear}
              onChange={(e) => setForm({ ...form, academicYear: e.target.value })}
              placeholder="2025/2026"
            />
          </Field>
          <Field label="Term" required>
            <Select value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value as Term })}>
              <option value="term_1">Term 1</option>
              <option value="term_2">Term 2</option>
              <option value="term_3">Term 3</option>
            </Select>
          </Field>
          <Field label="Due date" required hint="Arrears are measured from this">
            <Input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Applies to" hint="One group, or every student in the school">
          <Select
            value={form.groupId ?? ''}
            onChange={(e) => setForm({ ...form, groupId: e.target.value || null })}
          >
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </Select>
        </Field>

        <div>
          <p className={cn(styles.label, 'mb-1.5')}>Fee lines</p>
          <div className="flex flex-col gap-1.5">
            {form.items.map((item, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <Input
                  className="flex-1"
                  value={item.label}
                  onChange={(e) => setItem(i, { label: e.target.value })}
                  placeholder="Tuition"
                  aria-label={`Fee line ${i + 1} name`}
                />
                <Input
                  className="w-32 text-right tabular-nums"
                  value={item.amount}
                  onChange={(e) => setItem(i, { amount: e.target.value })}
                  placeholder="0.00"
                  inputMode="decimal"
                  aria-label={`Fee line ${i + 1} amount`}
                  invalid={item.amount.trim() !== '' && parseMajorToMinor(item.amount) === null}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  iconOnly
                  aria-label={`Remove fee line ${i + 1}`}
                  disabled={form.items.length === 1}
                  onClick={() => removeItem(i)}
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
          <Button type="button" variant="ghost" size="sm" className="mt-1.5" icon={<Plus size={13} />} onClick={addItem}>
            Add line
          </Button>
        </div>
      </form>
    </Modal>
  );
}
